import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Vg2Shell } from "./Vg2Shell";
import { RequestedOperationsPanel } from "@/components/vg2/RequestedOperations";
import {
  generateDecision,
  getDecision,
  postOwnerChoice,
  getDecidiOpId,
  newChoiceOpId,
  getDecidiChecklist,
  patchDecidiChecklistItem,
} from "@/lib/vg2Decidi";

/**
 * DECIDI page — Phase 5D.
 * Renders the owner-facing view derived server-side from CognitiveDecisionResult.
 * Never exposes SmartEva internals; no numeric score; semaphore is semantic.
 */
export default function Decidi() {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const [phase, setPhase] = React.useState("LOADING"); // LOADING/READY/BLOCKED_VALUATION/ERROR
  const [view, setView] = React.useState(null);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState(null);

  // §LAUNCH-A · la generazione sincrona di DECIDI a freddo può superare il
  // limite del gateway (~60s) e restituire 502 benché il backend completi e
  // PERSISTA la decisione. In quel caso NON rilanciamo la POST (per non
  // avviare una seconda generazione e un secondo costo AI): leggiamo lo stato
  // con sole GET finché la decisione compare, entro un'attesa massima.
  const POLL_INTERVAL_MS = 5000;
  const MAX_WAIT_MS = 150000;
  const aliveRef = React.useRef(true);
  React.useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);

  // Solo lettura: GET ripetute della decisione, nessuna nuova generazione.
  const pollForDecision = React.useCallback(async () => {
    setPhase("WAITING");
    setError(null);
    const deadline = Date.now() + MAX_WAIT_MS;
    while (Date.now() < deadline) {
      await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
      if (!aliveRef.current) return;
      try {
        const v = await getDecision(caseId);
        if (!aliveRef.current) return;
        setView(v);
        setPhase("READY");
        return;
      } catch (e) {
        const st = e?.response?.status;
        const code = e?.response?.data?.detail?.error_code;
        if (st === 409 && code === "VALUATION_REQUIRED") {
          setPhase("BLOCKED_VALUATION");
          return;
        }
        // 404 ⇒ non ancora pronta ⇒ continua a leggere (nessuna POST).
      }
    }
    if (aliveRef.current) setPhase("TIMEOUT");
  }, [caseId]);

  const load = React.useCallback(async () => {
    setPhase("LOADING");
    setError(null);
    try {
      // Try read-only fetch first (idempotent, cheap).
      try {
        const v = await getDecision(caseId);
        if (!v?.structured_request_ref?.stale) {
          setView(v);
          setPhase("READY");
          return;
        }
      } catch (e) {
        if (e?.response?.status !== 404) throw e;
      }
      // No decision yet → generate one (deterministic idempotent op_id).
      try {
        const v = await generateDecision(caseId, getDecidiOpId(caseId));
        setView(v);
        setPhase("READY");
        return;
      } catch (ge) {
        const gs = ge?.response?.status;
        // Timeout del gateway (502/503/504) o rete interrotta ⇒ la
        // generazione è quasi certamente ancora in corso lato server.
        // Passiamo alla lettura (polling), SENZA rilanciare la POST.
        const gatewayTimeout = !ge?.response || gs === 502 || gs === 503
          || gs === 504 || ge?.code === "ECONNABORTED";
        if (gatewayTimeout) {
          await pollForDecision();
          return;
        }
        throw ge; // fallimento definitivo ⇒ gestito sotto
      }
    } catch (e) {
      const status = e?.response?.status;
      const code = e?.response?.data?.detail?.error_code;
      if (status === 409 && code === "VALUATION_REQUIRED") {
        setPhase("BLOCKED_VALUATION");
      } else if (status === 404) {
        setError("Caso non trovato o non autorizzato.");
        setPhase("ERROR");
      } else {
        setError("Non riesco a preparare la tua analisi in questo momento.");
        setPhase("ERROR");
      }
    }
  }, [caseId, pollForDecision]);

  React.useEffect(() => { load(); }, [load]);

  async function submitChoice({ choice_type, selected_scenario_key, owner_reason }) {
    if (!view) return;
    setSubmitting(true);
    setError(null);
    try {
      const op = newChoiceOpId(caseId, choice_type, selected_scenario_key);
      const next = await postOwnerChoice(caseId, {
        decision_result_id: view.decision_result_id,
        choice_type,
        selected_scenario_key: selected_scenario_key || null,
        owner_reason: owner_reason || null,
        operation_id: op,
      });
      setView(next);
      // Post-choice orchestration: on successful confirm/alternative
      // (readiness AGISCI_READY or NEEDS_PREREQUISITE) the owner is
      // routed straight to AGISCI so the ActionPlan is materialised
      // without an extra click. DEFER remains on the DECIDI page.
      const r = next?.owner_decision?.readiness;
      if (choice_type !== "DEFER_DECISION"
          && (r === "AGISCI_READY" || r === "NEEDS_PREREQUISITE")) {
        try { window.scrollTo({ top: 0 }); } catch { /* ignore */ }
        navigate(`/vg2/agisci/${caseId}`);
      }
    } catch (e) {
      const status = e?.response?.status;
      const code = e?.response?.data?.detail?.error_code;
      if (status === 409 && code === "DECISION_VERSION_SUPERSEDED") {
        // §A-8/D1 · Una nuova DECIDI è nata mentre l'utente stava scegliendo.
        // Ricarichiamo la versione corrente (gli input restano conservati),
        // POI impostiamo l'avviso: load() esegue setError(null), quindi
        // l'avviso va impostato DOPO la ricarica per restare visibile.
        await load();
        setError("L’analisi è stata aggiornata mentre sceglievi. Abbiamo "
          + "ricaricato la versione corrente: conferma di nuovo la tua scelta.");
      } else {
        setError(code === "INVALID_OWNER_CHOICE"
          ? "La scelta non è coerente con l’analisi attuale. Riprova."
          : "Non riesco a registrare la tua scelta ora. Riprova tra qualche istante.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (phase === "LOADING") {
    return (
      <Vg2Shell>
        <div className="pt-24 text-vg2-muted" data-testid="decidi-loading">
          Sto preparando l’analisi decisionale…
        </div>
      </Vg2Shell>
    );
  }
  if (phase === "BLOCKED_VALUATION") {
    return (
      <Vg2Shell>
        <div className="pt-24" data-testid="decidi-blocked-valuation">
          <h1 className="font-display font-semibold text-3xl leading-tight mb-4">
            Prima della decisione serve la stima di valore
          </h1>
          <p className="text-vg2-muted mb-8 max-w-[600px]">
            Devi completare VALUTA prima di passare a DECIDI.
          </p>
          <button
            onClick={() => navigate(`/vg2/valuta/${caseId}`)}
            data-testid="decidi-goto-valuta"
            className="px-5 py-3 bg-vg2-red text-white rounded hover:bg-vg2-red-hover transition-colors"
          >
            Vai a VALUTA
          </button>
        </div>
      </Vg2Shell>
    );
  }
  if (phase === "WAITING") {
    return (
      <Vg2Shell>
        <div className="pt-24 max-w-[620px]" data-testid="decidi-waiting">
          <h1 className="font-display font-semibold text-2xl md:text-3xl leading-tight mb-4">
            Stiamo preparando il confronto
          </h1>
          <p className="text-vg2-muted mb-3">
            Stiamo preparando il confronto: può richiedere circa due minuti.
            I tuoi dati sono salvati.
          </p>
          <p className="text-vg2-muted text-sm flex items-center gap-2">
            <span className="inline-block w-2 h-2 rounded-full bg-vg2-red animate-pulse" />
            Attesa massima circa 150 secondi…
          </p>
        </div>
      </Vg2Shell>
    );
  }
  if (phase === "TIMEOUT") {
    return (
      <Vg2Shell>
        <div className="pt-24 max-w-[620px]" data-testid="decidi-timeout">
          <h1 className="font-display font-semibold text-2xl md:text-3xl leading-tight mb-4">
            Ci sta mettendo più del previsto
          </h1>
          <p className="text-vg2-muted mb-6">
            L’analisi è ancora in preparazione e i tuoi dati sono salvati.
            Puoi riprendere tra poco: non perderai nulla.
          </p>
          <button
            onClick={pollForDecision}
            data-testid="decidi-resume"
            className="px-5 py-3 bg-vg2-red text-white rounded hover:bg-vg2-red-hover transition-colors"
          >
            Riprendi
          </button>
        </div>
      </Vg2Shell>
    );
  }
  if (phase === "ERROR") {
    return (
      <Vg2Shell>
        <div className="pt-24" data-testid="decidi-error">
          <p role="alert" className="text-vg2-error mb-6">{error}</p>
          <button
            onClick={load}
            data-testid="decidi-retry"
            className="px-4 py-2 border border-vg2-line rounded hover:bg-vg2-subtle"
          >
            Riprova
          </button>
        </div>
      </Vg2Shell>
    );
  }

  return (
    <Vg2Shell>
      <DecidiPage view={view} onChoice={submitChoice} submitting={submitting} error={error} onViewChange={setView} />
    </Vg2Shell>
  );
}


// ---------- content sections --------------------------------------------

function DecidiPage({ view, onChoice, submitting, error, onViewChange }) {
  return (
    <div className="pt-12 pb-24" data-testid={`decidi-page-${view.resolution_type.toLowerCase()}`}>
      <p className="text-xs uppercase tracking-[0.14em] text-vg2-muted mb-4"
         data-testid="decidi-stage-label">{view.structured_request_ref ? "Decidi · Quali strade posso scegliere?" : "Decidi · Documenti e professionisti"}</p>

      <h1 className="font-display font-semibold text-3xl md:text-4xl leading-tight mb-4"
          data-testid="decidi-headline">
        {view.orientation_headline}
      </h1>
      {view.structured_request_ref?.interpretation_degraded && (
        <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 mb-4 max-w-[720px]"
           data-testid="decidi-degraded-banner">
          Richiesta letta in modalità semplificata: le operazioni e i numeri sono esplorativi e non vanno considerati
          una raccomandazione affidabile. Verifica le operazioni o riformula la richiesta più tardi.
        </p>
      )}
      {!view.structured_request_ref && (
        <p className="text-vg2-muted max-w-[620px] mb-2" data-testid="decidi-resolution-label">
          {view.resolution_label}
        </p>
      )}
      <p className="text-vg2-ink text-lg leading-relaxed max-w-[640px] mb-10"
         data-testid="decidi-resolve-summary">
        {view.resolve_summary}
      </p>

      {/* §M8.4 · DECIDI ricostruita come burocrazia (M7+).
          Le "Strade possibili" + matrice di confronto (ScenariosSection,
          ComparisonMatrix) duplicavano le alternative già presenti in
          VALUTA — sono state rimosse. Restano: la direzione scelta,
          l'elenco concreto dei documenti e professionisti necessari,
          e la conferma della decisione. */}

      <RequestedOperationsPanel view={view} caseId={view.case_id} onViewChange={onViewChange} />

      {/* Post-collaudo · con la Richiesta strutturata la scelta avviene in ANALIZZA e documenti/attività in AGISCI. */}
      {!view.structured_request_ref && <DecidiChecklistSection view={view} />}

      {view.critical_unknowns?.length > 0 && (
        <CriticalUnknownsSection items={view.critical_unknowns} />
      )}

      {!view.structured_request_ref && (
        <OwnerDecisionSection
          view={view}
          onChoice={onChoice}
          submitting={submitting}
          error={error}
        />
      )}
    </div>
  );
}


// §M8.5 · Checklist DECIDI · interattiva, backend-driven.
//
// Sostituisce la mappa hardcoded `_SCENARIO_BUREAUCRACY` con la lista
// ufficiale gestita dal backend (`decidi_checklist_data.py`). Per ogni
// documento/verifica il proprietario risponde "Ce l'ho" / "Non ce l'ho"
// / "Non so"; le risposte sono persistite lato server per (case_id,
// scenario_key) così AGISCI può filtrare la lista mostrando solo ciò
// che risulta ancora da reperire o verificare.
function DecidiChecklistSection({ view }) {
  const scenarios = view?.scenarios || [];
  const focus = scenarios.find((s) => s.is_owner_selected)
    || scenarios.find((s) => s.is_primary)
    || scenarios[0];
  const focusKey = focus?.scenario_key || null;
  const caseId = view?.case_id;

  const [state, setState] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);

  const load = React.useCallback(async () => {
    if (!caseId || !focusKey) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getDecidiChecklist(caseId, focusKey);
      setState(data);
    } catch (e) {
      setError("Non riesco a caricare l'elenco documenti in questo momento.");
    } finally {
      setLoading(false);
    }
  }, [caseId, focusKey]);

  React.useEffect(() => { load(); }, [load]);

  const updateItem = async (itemId, status) => {
    // optimistic
    setState((prev) => prev && ({
      ...prev,
      items: prev.items.map((it) =>
        it.item_id === itemId ? { ...it, status } : it
      ),
    }));
    try {
      const next = await patchDecidiChecklistItem(caseId, {
        scenarioKey: focusKey,
        itemId,
        status,
      });
      setState(next);
    } catch (e) {
      setError("Non riesco a salvare la risposta. Riprova.");
      // rollback on failure
      load();
    }
  };

  if (!focus) return null;

  return (
    <section className="mb-10" data-testid="decidi-checklist">
      <h2 className="font-display text-2xl font-medium mb-2">
        Cosa serve per procedere
      </h2>
      <p className="text-vg2-muted mb-6 max-w-[680px]"
         data-testid="decidi-checklist-focus">
        Direzione: <strong className="text-vg2-ink">{focus.title}</strong>.
        {" "}Per ogni documento o verifica indica il tuo stato attuale.
        Le risposte saranno riprese nel Piano d'Azione così da non
        ripetere ciò che hai già.
      </p>

      {loading && !state && (
        <p className="text-sm text-vg2-muted" data-testid="decidi-checklist-loading">
          Sto preparando l'elenco…
        </p>
      )}
      {error && (
        <p role="alert" className="text-vg2-error text-sm mb-4"
           data-testid="decidi-checklist-error">{error}</p>
      )}

      {state?.items?.length === 0 && !loading && (
        <p className="text-sm text-vg2-muted italic"
           data-testid="decidi-checklist-empty">
          Per questa direzione non sono richiesti documenti specifici in
          questa fase.
        </p>
      )}

      {state?.items?.length > 0 && (
        <>
          <ChecklistProgress counts={state.counts} total={state.items.length} />
          <div className="space-y-3 mt-4">
            {state.items.map((it) => (
              <ChecklistItemRow
                key={it.item_id}
                item={it}
                onChange={(status) => updateItem(it.item_id, status)}
              />
            ))}
          </div>
        </>
      )}
    </section>
  );
}


function ChecklistProgress({ counts, total }) {
  const answered = (counts?.have || 0) + (counts?.missing || 0) + (counts?.unknown || 0);
  return (
    <div className="flex items-center gap-3 flex-wrap text-xs text-vg2-muted"
         data-testid="decidi-checklist-progress">
      <span>{answered} / {total} risposte date</span>
      {counts?.have > 0 && (
        <span className="inline-flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-emerald-500" />
          {counts.have} già in mano
        </span>
      )}
      {counts?.missing > 0 && (
        <span className="inline-flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-red-500" />
          {counts.missing} da procurare
        </span>
      )}
      {counts?.unknown > 0 && (
        <span className="inline-flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-amber-500" />
          {counts.unknown} da verificare
        </span>
      )}
    </div>
  );
}


const _STATUS_LABEL = {
  have: "Ce l'ho",
  missing: "Non ce l'ho",
  unknown: "Non so",
};

function ChecklistItemRow({ item, onChange }) {
  const cur = item.status || null;
  const borderCls =
    cur === "have"    ? "border-emerald-400 bg-emerald-50/40" :
    cur === "missing" ? "border-red-400 bg-red-50/40" :
    cur === "unknown" ? "border-amber-400 bg-amber-50/40" :
                        "border-vg2-line";
  return (
    <div
      className={`border ${borderCls} rounded-lg p-4 transition-colors`}
      data-testid={`decidi-checklist-item-${item.item_id}`}
    >
      <p className="font-medium text-vg2-ink mb-1">{item.document}</p>
      <p className="text-sm text-vg2-muted mb-1">
        <span className="uppercase tracking-wider text-xs">Chi</span>
        {" · "}{item.professional}
      </p>
      <p className="text-sm text-vg2-muted mb-3">
        <span className="uppercase tracking-wider text-xs">Dove</span>
        {" · "}{item.where}
      </p>
      <div className="flex flex-wrap gap-2" role="radiogroup"
           aria-label={`Stato: ${item.document}`}>
        {(["have", "missing", "unknown"]).map((s) => {
          const active = cur === s;
          return (
            <button
              key={s}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => onChange(s)}
              data-testid={`decidi-checklist-item-${item.item_id}-${s}`}
              className={
                "px-3 py-1.5 rounded-full text-sm border transition-colors " +
                (active
                  ? (s === "have"
                      ? "bg-emerald-500 text-white border-emerald-500"
                      : s === "missing"
                      ? "bg-red-500 text-white border-red-500"
                      : "bg-amber-500 text-white border-amber-500")
                  : "border-vg2-line text-vg2-ink hover:bg-vg2-subtle")
              }
            >
              {_STATUS_LABEL[s]}
            </button>
          );
        })}
      </div>
    </div>
  );
}


// ---------- ancillary sections ------------------------------------------

function CriticalUnknownsSection({ items }) {
  return (
    <section className="mb-10" data-testid="decidi-critical-unknowns">
      <h2 className="font-display text-lg font-medium mb-3">Elementi da chiarire</h2>
      <ul className="space-y-3">
        {items.map((u, i) => (
          <li key={i} className="border border-vg2-line rounded p-4">
            <p className="font-medium text-sm mb-1">{u.topic}</p>
            <p className="text-sm text-vg2-muted">{u.why_it_matters}</p>
          </li>
        ))}
      </ul>
    </section>
  );
}


// ---------- owner decision controls -------------------------------------

const READINESS_LABEL = {
  AGISCI_READY: "Pronto per un piano d’azione.",
  NEEDS_PREREQUISITE: "Serve prima risolvere un aspetto critico.",
  DEFERRED: "Decisione rimandata.",
  NEEDS_DECISION_INFORMATION: "Servono ancora informazioni prima di scegliere.",
};

function OwnerDecisionSection({ view, onChoice, submitting, error }) {
  const navigate = useNavigate();
  const [choosing, setChoosing] = React.useState(null); // null | "alt" | "defer"
  const [selectedAlt, setSelectedAlt] = React.useState(null);
  const [reason, setReason] = React.useState("");

  const current = view.owner_decision;
  const superseded = view.superseded_previous_choice;
  const supersededDate = superseded?.created_at
    ? new Date(superseded.created_at).toLocaleDateString("it-IT")
    : null;
  const supersededScenario = superseded
    ? (superseded.selected_scenario_title
        || superseded.selected_scenario_key
        || (superseded.choice_type === "DEFER_DECISION"
              ? "decisione rimandata" : null))
    : null;
  const canShowAgisciCta = current && (
    current.readiness === "AGISCI_READY" ||
    current.readiness === "NEEDS_PREREQUISITE"
  );
  const nextStateMsg = current && !canShowAgisciCta ? (
    current.readiness === "DEFERRED"
      ? "Hai scelto di rimandare la decisione. Quando vuoi, torna qui per confermare o cambiare scelta."
      : "In DECIDI restano informazioni da chiarire: risolvile e poi torna a costruire il percorso."
  ) : null;

  return (
    <section className="mt-8 border-t border-vg2-line pt-8" data-testid="decidi-owner-choice">
      <h2 className="font-display text-xl font-medium mb-4">La tua decisione</h2>

      {superseded && !current && (
        <div className="mb-6 border border-amber-300 bg-amber-50 rounded p-4"
             data-testid="decidi-superseded-choice">
          <p className="text-sm font-medium text-amber-900 mb-1">
            L’analisi è stata aggiornata
          </p>
          <p className="text-sm text-amber-800">
            {supersededScenario
              ? <>La tua scelta precedente{supersededScenario
                    ? <> (<strong>{supersededScenario}</strong>)</> : null}
                  {supersededDate ? <> del {supersededDate}</> : null} è
                  conservata nello storico, ma non è più attiva su questa
                  nuova analisi.</>
              : <>La tua scelta precedente è conservata nello storico, ma
                  non è più attiva su questa nuova analisi.</>}
            {" "}Conferma una nuova scelta qui sotto per proseguire.
          </p>
        </div>
      )}

      {current && (
        <div className="mb-6 border border-vg2-line rounded p-4 bg-vg2-subtle"
             data-testid="decidi-owner-current">
          <p className="text-xs uppercase tracking-[0.14em] text-vg2-muted mb-2">Scelta attuale</p>
          <p className="text-sm font-medium mb-1">
            {current.choice_type === "DEFER_DECISION"
              ? "Hai deciso di rimandare la scelta"
              : `Hai scelto: ${current.selected_scenario_title}`}
          </p>
          <p className="text-sm text-vg2-muted">
            {READINESS_LABEL[current.readiness]}
          </p>
          {current.owner_reason && (
            <p className="mt-2 text-sm italic">“{current.owner_reason}”</p>
          )}
          {canShowAgisciCta && (
            <button
              type="button"
              onClick={() => navigate(`/vg2/agisci/${view.case_id}`)}
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-vg2-red text-white rounded hover:bg-vg2-red-hover transition-colors"
              data-testid="decidi-goto-agisci"
            >
              Passa ad AGISCI →
            </button>
          )}
          {nextStateMsg && (
            <p className="mt-4 text-sm text-vg2-muted" data-testid="decidi-next-state-msg">
              {nextStateMsg}
            </p>
          )}
        </div>
      )}

      {error && (
        <p role="alert" className="text-vg2-error text-sm mb-4"
           data-testid="decidi-owner-error">{error}</p>
      )}

      <div className="space-y-3">
        {view.can_confirm_primary && view.primary_scenario_key && (
          <button
            type="button"
            disabled={submitting}
            onClick={() => onChoice({
              choice_type: "CONFIRM_PRIMARY",
              selected_scenario_key: view.primary_scenario_key,
            })}
            className="w-full text-left border border-vg2-red text-vg2-red rounded p-4 hover:bg-vg2-red hover:text-white transition-colors disabled:opacity-60"
            data-testid="decidi-confirm-primary"
          >
            <p className="font-medium">Procedi con la direzione emergente</p>
            <p className="text-sm opacity-80 mt-1">
              {view.scenarios.find((s) => s.scenario_key === view.primary_scenario_key)?.title}
            </p>
          </button>
        )}

        <details
          className="border border-vg2-line rounded"
          open={choosing === "alt"}
          onToggle={(e) => setChoosing(e.target.open ? "alt" : null)}
          data-testid="decidi-choose-alternative-section"
        >
          <summary className="p-4 cursor-pointer font-medium text-sm"
                   data-testid="decidi-choose-alternative-summary">
            Scegli un’altra strada
          </summary>
          <div className="p-4 pt-0 space-y-2">
            {view.scenarios.map((s) => (
              <label
                key={s.scenario_key}
                className={`flex items-start gap-3 p-3 border rounded cursor-pointer ${
                  selectedAlt === s.scenario_key ? "border-vg2-red bg-vg2-subtle" : "border-vg2-line"
                }`}
                data-testid={`decidi-alt-option-${s.scenario_key.toLowerCase()}`}
              >
                <input
                  type="radio"
                  name="alt"
                  checked={selectedAlt === s.scenario_key}
                  onChange={() => setSelectedAlt(s.scenario_key)}
                  className="mt-1"
                />
                <span className="text-sm">
                  <span className="font-medium">{s.title}</span>
                  <span className="block text-vg2-muted">{s.strategy_description}</span>
                </span>
              </label>
            ))}
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Motivo (facoltativo): perché scegli questa strada?"
              className="w-full mt-2 p-3 border border-vg2-line rounded text-sm resize-none"
              rows={3}
              maxLength={1000}
              data-testid="decidi-alt-reason"
            />
            <button
              type="button"
              disabled={submitting || !selectedAlt}
              onClick={() => onChoice({
                choice_type: "CHOOSE_ALTERNATIVE",
                selected_scenario_key: selectedAlt,
                owner_reason: reason.trim() || null,
              })}
              className="mt-2 px-5 py-2 bg-vg2-red text-white rounded hover:bg-vg2-red-hover disabled:opacity-60"
              data-testid="decidi-submit-alternative"
            >
              Conferma questa scelta
            </button>
          </div>
        </details>

        <details
          className="border border-vg2-line rounded"
          open={choosing === "defer"}
          onToggle={(e) => setChoosing(e.target.open ? "defer" : null)}
          data-testid="decidi-defer-section"
        >
          <summary className="p-4 cursor-pointer font-medium text-sm"
                   data-testid="decidi-defer-summary">
            Non decido ancora
          </summary>
          <div className="p-4 pt-0 space-y-2">
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Motivo (facoltativo): perché preferisci aspettare?"
              className="w-full p-3 border border-vg2-line rounded text-sm resize-none"
              rows={3}
              maxLength={1000}
              data-testid="decidi-defer-reason"
            />
            <button
              type="button"
              disabled={submitting}
              onClick={() => onChoice({
                choice_type: "DEFER_DECISION",
                owner_reason: reason.trim() || null,
              })}
              className="px-5 py-2 border border-vg2-line rounded hover:bg-vg2-subtle disabled:opacity-60"
              data-testid="decidi-submit-defer"
            >
              Rimanda la decisione
            </button>
          </div>
        </details>
      </div>
    </section>
  );
}
